import { useQueryClient } from '@tanstack/react-query';
import type { TravelBookingMode } from '@hrms/shared';
import { tsr } from '../../../lib/api.js';

/**
 * Every Travel Desk query sits under this key, so one invalidation refreshes lists and details
 * (same convention as `HELPDESK_QUERY_KEY`). Build a key as `[...TRAVEL_QUERY_KEY, 'trips', id]`.
 */
export const TRAVEL_QUERY_KEY = ['travel'] as const;

/** Invalidate every travel query after a mutation. `const invalidate = useInvalidateTravel()`. */
export function useInvalidateTravel() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: TRAVEL_QUERY_KEY });
}

export interface TravelModuleStatusResult {
  /** True only once the server has said Travel Desk is switched on for this organisation. */
  enabled: boolean;
  /** True until the first answer arrives. While loading, `enabled` is false: render a skeleton,
   *  not the "not enabled" message. */
  isLoading: boolean;
  /** The admin's default for a new trip (`desk` or `self`), when known. */
  defaultBookingMode: TravelBookingMode | undefined;
}

/**
 * Is Travel Desk switched on for the signed-in user's organisation (D33)?
 *
 * Reads `GET /travel/admin/status` (`travelPolicy.getModuleStatus`), the one travel route with
 * no permission and no module guard: any signed-in user may ask, and it never creates data. Use it
 * to gate navigation entries and the reimbursement Travel tab; `TravelModuleGate` wraps it for a
 * whole screen.
 *
 * Fail closed: a failed request (network, 401) reads as `enabled: false` with `isLoading: false`.
 * Cached for five minutes, since the admin flips the switch rarely; the settings screen should
 * call `useInvalidateTravel()` after saving so the nav updates at once.
 */
export function useTravelModuleStatus(): TravelModuleStatusResult {
  const query = tsr.travelPolicy.getModuleStatus.useQuery({
    queryKey: [...TRAVEL_QUERY_KEY, 'module-status'],
    staleTime: 5 * 60_000,
  });
  const body = query.data?.status === 200 ? query.data.body : undefined;
  return {
    enabled: body?.enabled === true,
    isLoading: query.isLoading,
    defaultBookingMode: body?.defaultBookingMode,
  };
}
